import { SchultenMedia } from "@/services/DataService"
import { Api } from "@/services/ApiService"
import { ref } from "vue"
import { Preferences } from "@capacitor/preferences"
import { Device } from "@capacitor/device"
import { toast } from "vue3-toastify"
import pusherService from "@/services/PusherService"
import pushNotificationService from "@/services/PushNotificationService"
import { i18n } from "@/i18n"
import termsService from "@/services/TermsService"
import { clearDashboardCache } from "@/utils/dashboardCache"
import { clearQueryCache } from "@/lib/queryClient"
import { decideAuth } from "@/utils/authDecision"
import { useTenantStore } from "@/stores/tenantStore"

const TOKEN_KEY = "auth_token"
// Cached profile so the app can still render (header, etc.) when starting offline.
export const USER_CACHE_KEY = "auth_user"
const user = ref(null)
const isAuthenticated = ref(false)

export const useAuth = () => {
    const login = async (email: string, password: string) => {
        try {
            const info = await Device.getInfo()
            const device_name = info.name?.toString() || "Unknown Device"

            const data = await Api.postUnauthorized(SchultenMedia.API.auth.LOGIN, {
                email,
                password,
                device_name,
            })

            // Check if there was an error in the response
            if (data?.error) {
                console.error("Login error:", data?.message)
                return {
                    success: false,
                    message: data?.message || i18n.global.t("Inloggen mislukt"),
                }
            }

            // Check if token exists in response
            if (!data?.token) {
                return {
                    success: false,
                    message: i18n.global.t("Authenticatie mislukt - geen token ontvangen"),
                }
            }

            const token = data.token

            // Store token in preferences
            await Preferences.set({
                key: TOKEN_KEY,
                value: token,
            })

            // Cache the profile for offline startup.
            await Preferences.set({
                key: USER_CACHE_KEY,
                value: JSON.stringify(data.user),
            })

            // Update global state
            SchultenMedia.User = data.user
            SchultenMedia.Authenticated = true
            SchultenMedia.Bearer = `Bearer ${token}`
            isAuthenticated.value = true
            user.value = data.user
            
            // Clear terms cache to force re-check
            termsService.clearCache();
            await clearDashboardCache();
            await clearQueryCache();
            // The tenant feature flags still in memory belong to the account we
            // just left, not to the one signing in now.
            await useTenantStore().clearFeatures();

            // Subscribe to user's Pusher channel
            if (data.user?.id) {
                pusherService.initialize();
                const subscriptionSuccess = pusherService.subscribeToUserChannel(data.user.id)
                if (subscriptionSuccess) {
                    console.log(`Subscribed to user channel: user.${data.user.id}`)
                } else {
                    console.warn('Failed to subscribe to user channel')
                }
            }

            // Initialize push notifications
            pushNotificationService.initialize();

            return {
                success: true,
                message: i18n.global.t("Succesvol ingelogd"),
                user: data.user,
            }
        } catch (error) {
            console.error("Login exception:", error)
            return {
                success: false,
                message: i18n.global.t("Er is een onverwachte fout opgetreden tijdens het inloggen"),
            }
        }
    }

    // Log in with an already-issued token (used by the admin debug-impersonation
    // hand-off, where the app receives a token instead of credentials). Mirrors
    // the storage/side-effects of login() so the session behaves identically.
    const loginWithToken = async (token: string, profile: any) => {
        try {
            await Preferences.set({ key: TOKEN_KEY, value: token })
            await Preferences.set({ key: USER_CACHE_KEY, value: JSON.stringify(profile) })

            SchultenMedia.User = profile
            SchultenMedia.Authenticated = true
            SchultenMedia.Bearer = `Bearer ${token}`
            isAuthenticated.value = true
            user.value = profile

            termsService.clearCache()
            await clearDashboardCache()
            await clearQueryCache()
            await useTenantStore().clearFeatures()

            if (profile?.id) {
                pusherService.initialize()
                pusherService.subscribeToUserChannel(profile.id)
            }

            pushNotificationService.initialize()

            return { success: true, user: profile }
        } catch (error) {
            console.error("loginWithToken exception:", error)
            return { success: false, message: i18n.global.t("Authentication failed") }
        }
    }

    const sentResetMail = async (email: string) => {

        try {
            const data = await Api.postUnauthorized(SchultenMedia.API.auth.FORGOT_PASSWORD, {
                email
            })

            // Check if there was an error in the response
            if (data?.error) {
                return {
                    success: false,
                    message: data?.message || i18n.global.t("Wachtwoord vergeten mislukt"),
                }
            }

            return {
                success: true,
                message: data?.message || i18n.global.t("Verificatie OTP is verzonden naar je e-mail."),
                user: data.user,
            }
        } catch (error) {
            return {
                success: false,
                message: i18n.global.t("Wachtwoord vergeten mislukt"),
            }
        }
    }

    const verifyOtp = async (email: string, otp: string) => {
        try {
            const data = await Api.postUnauthorized(SchultenMedia.API.auth.VERIFY_OTP, {
                email,
                otp,
            })

            // Check if there was an error in the response
            if (data?.error) {
                return {
                    success: false,
                    message: data?.message || i18n.global.t("OTP verificatie mislukt"),
                }
            }

            return {
                success: true,
                message: data?.message || i18n.global.t("OTP succesvol geverifieerd"),
            }
        } catch (error) {
            console.error("OTP verification exception:", error)
            return {
                success: false,
                message: i18n.global.t("Er is een onverwachte fout opgetreden tijdens OTP verificatie"),
            }
        }
    }

    const resetPassword = async (email: string, otp: string, password: string, passwordConfirmation: string) => {
        try {
            const data = await Api.postUnauthorized(SchultenMedia.API.auth.RESET_PASSWORD, {
                email,
                otp,
                password,
                password_confirmation: passwordConfirmation,
            })

            // Check if there was an error in the response
            if (data?.error) {
                return {
                    success: false,
                    message: data?.message || i18n.global.t("Wachtwoord resetten mislukt"),
                }
            }

            return {
                success: true,
                message: data?.message || i18n.global.t("Wachtwoord succesvol gereset"),
            }
        } catch (error) {
            console.error("Password reset exception:", error)
            return {
                success: false,
                message: i18n.global.t("Er is een onverwachte fout opgetreden tijdens het resetten van het wachtwoord"),
            }
        }
    }

    // Helper functions for email encoding/decoding
    const encodeEmail = (email: string): string => {
        return btoa(email) // Base64 encode
    }

    const decodeEmail = (encodedEmail: string): string => {
        try {
            return atob(encodedEmail) // Base64 decode
        } catch (error) {
            console.error("Error decoding email:", error)
            return ""
        }
    }
    const logout = async () => {
        try {
            // Unsubscribe from Pusher channel before logout
            pusherService.unsubscribeFromUserChannel()

            // Cleanup push notifications
            pushNotificationService.cleanup()

            await Preferences.remove({ key: TOKEN_KEY })
            await Preferences.remove({ key: USER_CACHE_KEY })
            user.value = null
            isAuthenticated.value = false
            SchultenMedia.User = null
            SchultenMedia.Authenticated = false
            SchultenMedia.Bearer = ""
            
            // Clear terms cache
            termsService.clearCache();
            await clearDashboardCache();
            await clearQueryCache();
            await useTenantStore().clearFeatures();

            toast.success(i18n.global.t("Succesvol uitgelogd"), {
                position: "top-center",
                autoClose: 2000,
            })

            return true
        } catch (error) {
            console.error("Logout error:", error)

            toast.error(i18n.global.t("Fout tijdens uitloggen"), {
                position: "top-center",
                autoClose: 3000,
            })

            return false
        }
    }

    const checkAuth = async () => {
        try {
            // Check if token exists in preferences
            const { value: token } = await Preferences.get({ key: TOKEN_KEY })

            if (!token) {
                return false
            }

            // Set bearer token
            SchultenMedia.Bearer = `Bearer ${token}`

            // Verify token with API (status-aware: distinguishes offline from 401)
            const result = await Api.verifyToken(SchultenMedia.API.auth.VERIFY_TOKEN)
            const outcome = decideAuth(result)

            // Token genuinely rejected by the server → really log out.
            if (outcome.action === "logout") {
                await Preferences.remove({ key: TOKEN_KEY })
                await Preferences.remove({ key: USER_CACHE_KEY })
                SchultenMedia.Bearer = ""
                SchultenMedia.Authenticated = false
                isAuthenticated.value = false

                toast.warning(i18n.global.t("Sessie verlopen. Log opnieuw in."), {
                    position: "top-center",
                    autoClose: 3000,
                })

                return false
            }

            // Couldn't reach the server (offline / transient 5xx): trust the
            // stored token so the app stays usable. Restore the cached profile
            // so views that read SchultenMedia.User keep working offline.
            if (outcome.action === "offline") {
                const cached = await Preferences.get({ key: USER_CACHE_KEY })
                if (cached.value) {
                    const cachedUser = JSON.parse(cached.value)
                    SchultenMedia.User = cachedUser
                    user.value = cachedUser
                }
                SchultenMedia.Authenticated = true
                isAuthenticated.value = true
                return true
            }

            // Verified online — update + persist state.
            const data = result as { ok: true; data: any }
            SchultenMedia.User = outcome.user
            SchultenMedia.Authenticated = true
            isAuthenticated.value = true
            user.value = outcome.user

            // Cache the profile so the offline path above has something to show.
            await Preferences.set({ key: USER_CACHE_KEY, value: JSON.stringify(outcome.user) })

            // Clear terms cache to force re-check
            termsService.clearCache();
            await clearDashboardCache();
            await clearQueryCache();

            // Subscribe to user's Pusher channel if not already subscribed
            if (data?.data.user?.id) {
                pusherService.initialize();
                const subscriptionSuccess = pusherService.subscribeToUserChannel(data?.data.user.id)
                if (subscriptionSuccess) {
                    console.log(`Subscribed to user channel: user.${data?.data.user.id}`)
                } else {
                    console.warn('Failed to subscribe to user channel')
                }
            }

            // Initialize push notifications
            pushNotificationService.initialize();

            return true
        } catch (error) {
            // Only unexpected/local errors (e.g. Preferences read) reach here —
            // network failures are handled above and never throw. Do NOT delete
            // the token: if we already consider ourselves authenticated, stay in.
            console.error("Auth check error:", error)
            return SchultenMedia.Authenticated === true
        }
    }

    // Lightweight profile re-fetch. Unlike checkAuth() this does NOT clear
    // caches, re-subscribe Pusher, re-init push, or show a session-expiry toast
    // — it only refreshes SchultenMedia.User from the server (e.g. after an
    // OAuth integration like Garmin links and the app returns via deep link, so
    // the integrations modal reflects the new connection). Never wipes the
    // current user on a network/transient failure; returns the fresh user or null.
    const refreshUser = async () => {
        try {
            const { value: token } = await Preferences.get({ key: TOKEN_KEY })
            if (!token) {
                return null
            }

            SchultenMedia.Bearer = `Bearer ${token}`

            const result = await Api.verifyToken(SchultenMedia.API.auth.VERIFY_TOKEN)
            const outcome = decideAuth(result)

            // Only a confirmed online verification updates state. Offline/5xx/401
            // leave the existing user intact (this isn't an auth gate).
            if (outcome.action !== "authenticated") {
                return null
            }

            SchultenMedia.User = outcome.user
            user.value = outcome.user
            await Preferences.set({ key: USER_CACHE_KEY, value: JSON.stringify(outcome.user) })

            return outcome.user
        } catch (error) {
            console.error("refreshUser error:", error)
            return null
        }
    }

    const getConnectionUrl = async (name: string, returnOrigin?: string) => {
        try {
            let connectUrl = SchultenMedia.API.auth.CONNECT.replace(":name", name)
            if (returnOrigin) {
                connectUrl += `?return_origin=${encodeURIComponent(returnOrigin)}`
            }
            const responseResult = await Api.get(connectUrl)
            if (!responseResult.ok) {
                return {
                    success: false,
                    message: i18n.global.t("Could not connect. Please try again."),
                    url: null,
                }
            }

            const response = responseResult.data

            if (response?.error) {
                return {
                    success: false,
                    message: i18n.global.t("Could not connect. Please try again."),
                    url: null,
                }
            }

            return {
                success: true,
                message: "OAuth redirect URL succesvol ontvangen.",
                url: response?.url,
            }
        } catch (error) {
            return {
                success: false,
                message: i18n.global.t("Could not connect. Please try again."),
                url: null,
            }
        }
    }

    const disconnectAccount = async (name: string) => {
        try {
            const response = await Api.delete(SchultenMedia.API.auth.DISCONNECT.replace(":name", name))

            if (response?.error) {
                return {
                    success: false,
                    message: i18n.global.t("settings.integrations.disconnectFailed"),
                }
            }

            return {
                success: true,
                message: "Account succesvol ontkoppeld.",
            }
        } catch (error) {
            return {
                success: false,
                message: i18n.global.t("settings.integrations.disconnectFailed"),
            }
        }
    }

    return {
        user,
        isAuthenticated,
        login,
        loginWithToken,
        logout,
        checkAuth,
        refreshUser,
        getConnectionUrl,
        disconnectAccount,
        sentResetMail,
        verifyOtp,
        resetPassword,
        encodeEmail,
        decodeEmail,
    }
}
